<!-- Right-side quick-view/quick-edit panel for a domain object, opened from the
     entities canvas (EntitiesDiagram.vue) instead of navigating straight to the
     full BusinessEntityPage. View mode shows description and relationships;
     confidentiality and attributes are left off this panel, viewable only from
     "View full details". Edit mode is a curated subset of the full page's form
     (name, description, relationships). Which mode shows is driven by the same
     app-wide Edit/View toggle (useEditManager) as every other entity page —
     there's no separate edit button in the panel itself.

     Two things are deliberately NOT taken from the entities list:
       - the description. The list only carries `short_description`, which is a
         LOSSY server-side preview (markdown stripped, truncated to ~256 chars,
         no ellipsis). Editing that and saving it back would replace the real
         description with its own shadow, so the real markdown is fetched
         through the artefact link instead — same source the full page uses.
       - the save. Saves go through syncStore.queueSync, the app-wide pipeline
         that gives changed-fields-only payloads, retries, Sentry capture, the
         failed-sync recovery UI, OPFS drafts, cross-tab broadcast and cache
         invalidation. A bare api.* call would silently drop all of it.
     Saves are automatic (throttled, no Save button), matching every other
     autosaving surface in the app. -->
<template>
    <Transition name="entity-panel">
        <aside
            v-if="drawerOpen"
            ref="panelRef"
            class="entity-panel"
            :style="{ paddingTop: panelOffset }"
            data-testid="domain-object-panel"
            tabindex="-1"
            @keydown.esc="closeDrawer"
        >
            <div class="entity-panel-header">
                <span class="entity-panel-title">{{
                    entity?.name ?? "Domain object"
                }}</span>
                <Button
                    icon="pi pi-times"
                    text
                    rounded
                    size="small"
                    aria-label="Close panel"
                    @click="closeDrawer"
                />
            </div>

            <div class="entity-panel-body">
                <!-- Terminal state: the id resolves against a DOMAIN-SCOPED
                     list, so "not in the list and nothing in flight" means it
                     is not coming. Must not be a spinner — an unbounded
                     spinner is a promise the panel cannot keep. -->
                <template v-if="entityUnavailable">
                    <div
                        class="panel-unavailable"
                        data-testid="panel-unavailable"
                    >
                        <i class="pi pi-exclamation-circle" />
                        <p>
                            This domain object isn't in the current domain any
                            more. It may have been moved or deleted.
                        </p>
                        <Button
                            label="Close"
                            size="small"
                            outlined
                            data-testid="panel-unavailable-close"
                            @click="closeDrawer"
                        />
                    </div>
                </template>

                <template v-else-if="!entity">
                    <div class="panel-empty">
                        <ProgressSpinner />
                    </div>
                </template>

                <template v-else-if="isEditing">
                    <div class="panel-form">
                        <div class="field">
                            <label for="panel-entity-name">Name</label>
                            <InputText
                                id="panel-entity-name"
                                v-model="formName"
                                size="small"
                                class="w-full"
                                :invalid="!!nameError"
                                aria-describedby="panel-entity-name-error"
                            />
                            <!-- An empty name is a field-level problem, so it
                                 is reported on the field and only `name` is
                                 held back from the payload — it must not
                                 silently abort the whole save (description and
                                 relationship edits included). -->
                            <small
                                v-if="nameError"
                                id="panel-entity-name-error"
                                class="field-error"
                                data-testid="panel-name-error"
                                >{{ nameError }}</small
                            >
                        </div>
                        <div class="field">
                            <label for="panel-entity-description"
                                >Description</label
                            >
                            <div
                                v-if="descriptionState === 'loading'"
                                class="field-loading"
                                data-testid="panel-description-loading"
                            >
                                <ProgressSpinner
                                    style="width: 1.5rem; height: 1.5rem"
                                />
                                <span>Loading description…</span>
                            </div>
                            <div
                                v-else-if="descriptionState === 'error'"
                                class="field-error-block"
                                data-testid="panel-description-error"
                            >
                                <p>
                                    The description couldn't be loaded, so it
                                    isn't editable here — editing it now would
                                    risk overwriting it.
                                </p>
                                <Button
                                    label="Retry"
                                    size="small"
                                    text
                                    data-testid="panel-description-retry"
                                    @click="loadDescription"
                                />
                            </div>
                            <Textarea
                                v-else
                                id="panel-entity-description"
                                v-model="formDescription"
                                rows="5"
                                size="small"
                                class="w-full"
                            />
                        </div>
                        <div class="relationships-section">
                            <h3 class="section-title">Relationships</h3>

                            <ul
                                v-if="formRelationships.length"
                                class="relationship-list"
                            >
                                <li
                                    v-for="(rel, index) in formRelationships"
                                    :key="`edit-rel-${index}`"
                                    class="relationship-row"
                                >
                                    <i
                                        :class="
                                            rel.direction ===
                                            RelationshipDirection.OUTGOING
                                                ? 'pi pi-arrow-right'
                                                : 'pi pi-arrow-left'
                                        "
                                        class="relationship-direction-icon"
                                    />
                                    <span class="relationship-row-sentence">
                                        <template
                                            v-if="
                                                rel.direction ===
                                                RelationshipDirection.OUTGOING
                                            "
                                        >
                                            <span class="relationship-type">{{
                                                rel.type
                                            }}</span>
                                            <span class="entity-reference">{{
                                                relatedEntityName(rel.other_id)
                                            }}</span>
                                        </template>
                                        <template v-else>
                                            <span class="entity-reference">{{
                                                relatedEntityName(rel.other_id)
                                            }}</span>
                                            <span class="relationship-type">{{
                                                rel.type
                                            }}</span>
                                        </template>
                                    </span>
                                    <Button
                                        icon="pi pi-times"
                                        text
                                        rounded
                                        size="small"
                                        severity="danger"
                                        aria-label="Remove relationship"
                                        data-testid="panel-remove-relationship"
                                        @click="removeRelationship(rel)"
                                    />
                                </li>
                            </ul>
                            <span v-else class="summary-value muted"
                                >No relationships yet.</span
                            >

                            <div class="rel-add-section">
                                <Button
                                    v-if="!showAddRelationshipForm"
                                    icon="pi pi-plus"
                                    label="Add relationship"
                                    outlined
                                    size="small"
                                    class="w-full"
                                    data-testid="panel-toggle-add-relationship"
                                    @click="openAddRelationshipForm"
                                />

                                <div v-else class="rel-add-form">
                                    <p class="field-label">New relationship</p>
                                    <SelectButton
                                        v-model="newRelationshipDirection"
                                        :options="relationshipDirectionOptions"
                                        :allowEmpty="false"
                                        optionLabel="label"
                                        optionValue="value"
                                        size="small"
                                    />
                                    <!-- Outgoing reads as "this entity → type →
                                         target" (type first, then target); incoming
                                         reads as "target → type → this entity" (target
                                         first, then type) — the fields swap order to
                                         match, same as the two directions' view-mode
                                         sentences below. -->
                                    <template
                                        v-if="
                                            newRelationshipDirection ===
                                            RelationshipDirection.INCOMING
                                        "
                                    >
                                        <Select
                                            v-model="newRelationship.other_id"
                                            :options="
                                                availableRelationshipTargets
                                            "
                                            optionLabel="name"
                                            optionValue="identity"
                                            filter
                                            filterPlaceholder="Search entities"
                                            :filterFields="['name']"
                                            autoFilterFocus
                                            resetFilterOnHide
                                            size="small"
                                            overlayClass="rel-add-dropdown-overlay"
                                            placeholder="Select entity"
                                            class="w-full"
                                        />
                                        <Select
                                            v-model="newRelationship.type"
                                            :options="relationshipTypes"
                                            optionLabel="label"
                                            optionValue="value"
                                            editable
                                            size="small"
                                            overlayClass="rel-add-dropdown-overlay"
                                            placeholder="Select or type a relationship"
                                            class="w-full"
                                        />
                                    </template>
                                    <template v-else>
                                        <Select
                                            v-model="newRelationship.type"
                                            :options="relationshipTypes"
                                            optionLabel="label"
                                            optionValue="value"
                                            editable
                                            size="small"
                                            overlayClass="rel-add-dropdown-overlay"
                                            placeholder="Select or type a relationship"
                                            class="w-full"
                                        />
                                        <Select
                                            v-model="newRelationship.other_id"
                                            :options="
                                                availableRelationshipTargets
                                            "
                                            optionLabel="name"
                                            optionValue="identity"
                                            filter
                                            filterPlaceholder="Search entities"
                                            :filterFields="['name']"
                                            autoFilterFocus
                                            resetFilterOnHide
                                            size="small"
                                            overlayClass="rel-add-dropdown-overlay"
                                            placeholder="Select entity"
                                            class="w-full"
                                        />
                                    </template>
                                    <div class="rel-add-form-actions">
                                        <Button
                                            label="Cancel"
                                            text
                                            size="small"
                                            data-testid="panel-cancel-add-relationship"
                                            @click="cancelAddRelationshipForm"
                                        />
                                        <Button
                                            label="Add relationship"
                                            size="small"
                                            data-testid="panel-add-relationship"
                                            :disabled="!canAddRelationship"
                                            @click="addRelationship"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <p class="panel-form-hint">
                            Attributes and other details can be edited from the
                            full page.
                        </p>
                    </div>
                </template>

                <template v-else>
                    <div class="panel-summary">
                        <div class="summary-section">
                            <h3 class="section-title">Description</h3>
                            <div
                                v-if="descriptionState === 'loading'"
                                class="field-loading"
                            >
                                <ProgressSpinner
                                    style="width: 1.5rem; height: 1.5rem"
                                />
                            </div>
                            <!-- Rendered from the real markdown, not
                                 `short_description`. `html: false` on the
                                 MarkdownIt instance escapes any raw HTML in the
                                 source, so v-html cannot inject markup here. -->
                            <div
                                v-else-if="renderedDescription"
                                class="summary-desc markdown-body"
                                data-testid="panel-description"
                                v-html="renderedDescription"
                            />
                            <p
                                v-else-if="descriptionState === 'error'"
                                class="summary-desc muted"
                            >
                                The description couldn't be loaded.
                            </p>
                            <p v-else class="summary-desc muted">
                                No description yet.
                            </p>
                        </div>

                        <div class="summary-section">
                            <h3 class="section-title">Relationships</h3>
                            <template
                                v-if="
                                    outgoingRelationships.length ||
                                    incomingRelationships.length
                                "
                            >
                                <div
                                    v-if="outgoingRelationships.length"
                                    class="relationship-group"
                                >
                                    <h4 class="field-label">Outgoing</h4>
                                    <ul class="relationship-list">
                                        <li
                                            v-for="(
                                                rel, index
                                            ) in outgoingRelationships"
                                            :key="`out-${index}`"
                                            class="relationship-row"
                                        >
                                            <i
                                                class="pi pi-arrow-right relationship-direction-icon"
                                            />
                                            <span
                                                class="relationship-row-sentence"
                                            >
                                                {{ entity.name }}
                                                <span
                                                    class="relationship-type"
                                                    >{{ rel.type }}</span
                                                >
                                                <span
                                                    class="entity-reference"
                                                    >{{
                                                        relatedEntityName(
                                                            rel.other_id
                                                        )
                                                    }}</span
                                                >
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                                <div
                                    v-if="incomingRelationships.length"
                                    class="relationship-group"
                                >
                                    <h4 class="field-label">Incoming</h4>
                                    <ul class="relationship-list">
                                        <li
                                            v-for="(
                                                rel, index
                                            ) in incomingRelationships"
                                            :key="`in-${index}`"
                                            class="relationship-row"
                                        >
                                            <i
                                                class="pi pi-arrow-left relationship-direction-icon"
                                            />
                                            <span
                                                class="relationship-row-sentence"
                                            >
                                                <span
                                                    class="entity-reference"
                                                    >{{
                                                        relatedEntityName(
                                                            rel.other_id
                                                        )
                                                    }}</span
                                                >
                                                <span
                                                    class="relationship-type"
                                                    >{{ rel.type }}</span
                                                >
                                                {{ entity.name }}
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </template>
                            <span v-else class="summary-value muted"
                                >No relationships yet.</span
                            >
                        </div>
                    </div>
                </template>
            </div>

            <!-- Pinned, not the last thing in the scrolling body: on a long
                 object these actions were only reachable after scrolling past
                 every attribute and relationship. -->
            <div v-if="!entityUnavailable" class="entity-panel-footer">
                <Button
                    icon="pi pi-window-maximize"
                    label="View full details"
                    text
                    size="small"
                    data-testid="panel-open-full-page"
                    @click="openFullPage"
                />
                <!-- Edit mode only, matching the objects table: view mode has
                     no destructive actions anywhere in the app. `canEdit` as
                     well, because nothing resets `isEditing` when a release
                     becomes active — a panel left in edit mode would otherwise
                     keep offering to delete live data while the user browses a
                     historical release. -->
                <Button
                    v-if="isEditing && canEdit"
                    icon="pi pi-trash"
                    label="Delete"
                    text
                    size="small"
                    severity="danger"
                    :loading="isDeleting"
                    data-testid="panel-delete-object"
                    @click="confirmDelete"
                />
            </div>
        </aside>
    </Transition>
</template>

<script setup lang="ts">
import { throttle } from "lodash-es";
import MarkdownIt from "markdown-it";
import { storeToRefs } from "pinia";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import ProgressSpinner from "primevue/progressspinner";
import Select from "primevue/select";
import SelectButton from "primevue/selectbutton";
import Textarea from "primevue/textarea";
import { useConfirm } from "primevue/useconfirm";
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { FILE_SYNC_THROTTLE_MS } from "@/composables/draft-manager";
import { useEditManager } from "@/composables/edit-manager";
import { registerFlush, unregisterFlush } from "@/composables/pending-flush";
import { useRightPanelOffset } from "@/composables/right-panel-offset";
import {
    isAbortError,
    useLatestRequest,
} from "@/composables/use-latest-request";
import { applyCanvasPatches } from "@/lib/canvas-overlay";
import { CONFIRM_GROUP } from "@/lib/confirm-groups";
import { sortEntitiesByName } from "@/lib/entity-sort";
import {
    type BusinessEntityRelationship,
    RelationshipDirection,
} from "@/lib/openapi/gen";
import { followLink } from "@/lib/openapi/helpers";
import { relationshipTypes } from "@/lib/relationship-types";
import { withThreadRevision } from "@/lib/thread-revision-link";
import { useBusinessEntitiesStore } from "@/stores/business-entities-store";
import { useCanvasSaveStatusStore } from "@/stores/canvas-save-status";
import { useDomainObjectDrawerStore } from "@/stores/domain-object-drawer-store";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { useReleaseStore } from "@/stores/release-store";
import { useSyncStore } from "@/stores/sync-store";

const route = useRoute();
const router = useRouter();

const drawerStore = useDomainObjectDrawerStore();
const { drawerOpen, drawerEntityId } = storeToRefs(drawerStore);
const { closeDrawer } = drawerStore;

const entitiesStore = useBusinessEntitiesStore();
const syncStore = useSyncStore();
const canvasSaveStatus = useCanvasSaveStatusStore();
const { markPending: markCanvasSavePending } = canvasSaveStatus;
const releaseStore = useReleaseStore();
const { domain } = useCurrentEntityManager();
const { canEdit, isEditing } = useEditManager();
const confirm = useConfirm();
const { panelOffset } = useRightPanelOffset();

const panelRef = ref<HTMLElement | null>(null);

// Read through the same optimistic overlay the canvas draws from. This panel
// sits directly beside the canvas, so without it a rename in flight shows the
// new name in the input, the old one in the header here, and the new one on
// the node — three answers to the same question for the length of a save.
const canvasEntities = computed(() =>
    applyCanvasPatches(entitiesStore.entities, canvasSaveStatus.patches)
);

// Name/relationships come from the canvas's already-loaded list; the
// description does NOT (see the file header).
const entity = computed(() =>
    canvasEntities.value.find((e) => e.identity === drawerEntityId.value)
);

// "Asked for, not present, and nothing in flight that could still produce it."
// Distinguishing this from "still loading" is what keeps the panel from
// showing a spinner that never resolves.
const entityUnavailable = computed(
    () =>
        !!drawerEntityId.value &&
        !entity.value &&
        !entitiesStore.isLoading &&
        entitiesStore.entities.length > 0
);

const outgoingRelationships = computed(
    () =>
        entity.value?.relationships.filter(
            (r) => r.direction === RelationshipDirection.OUTGOING
        ) ?? []
);
const incomingRelationships = computed(
    () =>
        entity.value?.relationships.filter(
            (r) => r.direction === RelationshipDirection.INCOMING
        ) ?? []
);
const relatedEntityName = (identity: string) =>
    canvasEntities.value.find((e) => e.identity === identity)?.name ?? identity;

const formName = ref("");
const formDescription = ref("");

// Working copy of relationships for edit mode, separate from
// entity.value.relationships (which is read straight off the shared canvas
// entities list — mutating it directly would leak an unsaved edit into what
// the canvas itself renders).
const formRelationships = ref<BusinessEntityRelationship[]>([]);

// ---------------------------------------------------------------------------
// Description: the real markdown, fetched through the artefact link.
// ---------------------------------------------------------------------------

// `savedDescription` is the baseline the form is diffed against. It is NEVER
// seeded from `short_description` — that value is a lossy preview and writing
// it back would truncate the real description to ~256 chars of plain text.
const savedDescription = ref("");
const descriptionState = ref<"empty" | "loading" | "ready" | "error">("empty");
const descriptionRequest = useLatestRequest();

const md = new MarkdownIt({ html: false, linkify: true });
const renderedDescription = computed(() =>
    descriptionState.value === "ready" && savedDescription.value.trim()
        ? md.render(savedDescription.value)
        : ""
);

const revisionQuery = computed(() =>
    releaseStore.activeRevision
        ? new URLSearchParams({ revision: releaseStore.activeRevision })
        : undefined
);

const loadDescription = async () => {
    const target = entity.value;
    if (!target) return;

    // An entity with no description artefact has nothing to fetch — that is a
    // legitimately empty description, not an error.
    if (!target.description) {
        savedDescription.value = "";
        formDescription.value = "";
        descriptionState.value = "ready";
        return;
    }

    const { token, signal } = descriptionRequest.begin();
    descriptionState.value = "loading";
    try {
        const { response, data } = await followLink<File | string>(
            target.description,
            "get",
            undefined,
            signal,
            revisionQuery.value
        );

        // A newer request superseded us — commit nothing.
        if (!descriptionRequest.isCurrent(token)) return;

        if (!response.ok) {
            descriptionState.value = "error";
            return;
        }
        // followLink's `data` is not reliably a Blob — depending on how the
        // client parses the response it can come back as a plain string (see
        // the same defensive handling in fetchMarkdownArtefact).
        const text =
            data instanceof Blob
                ? await data.text()
                : typeof data === "string"
                  ? data
                  : "";
        if (!descriptionRequest.isCurrent(token)) return;

        savedDescription.value = text;
        formDescription.value = text;
        descriptionState.value = "ready";
    } catch (e) {
        if (isAbortError(e)) return;
        if (descriptionRequest.isCurrent(token)) {
            descriptionState.value = "error";
        }
    }
};

// ---------------------------------------------------------------------------
// Relationship editing
// ---------------------------------------------------------------------------

const showAddRelationshipForm = ref(false);
const relationshipDirectionOptions = [
    { label: "Outgoing", value: RelationshipDirection.OUTGOING },
    { label: "Incoming", value: RelationshipDirection.INCOMING },
];
const newRelationshipDirection = ref<RelationshipDirection>(
    RelationshipDirection.OUTGOING
);
const newRelationship = ref<{ type: string; other_id: string | null }>({
    type: "",
    other_id: null,
});

// Target-entity options for whichever direction is currently selected in the
// add form: exclude the current entity and any entity already related in that
// direction (mirrors BusinessEntityPage.vue's
// availableEntities/availableIncomingEntities).
const availableRelationshipTargets = computed(() => {
    const current = entity.value;
    if (!current) return [];
    return sortEntitiesByName(
        canvasEntities.value.filter(
            (e) =>
                e.identity !== current.identity &&
                !formRelationships.value.some(
                    (r) =>
                        r.other_id === e.identity &&
                        r.direction === newRelationshipDirection.value
                )
        )
    );
});

// No self-check needed: availableRelationshipTargets already excludes the
// current entity, so a selected target can never be it.
const canAddRelationship = computed(
    () => !!newRelationship.value.type && !!newRelationship.value.other_id
);

const openAddRelationshipForm = () => {
    showAddRelationshipForm.value = true;
};

const resetAddRelationshipForm = () => {
    showAddRelationshipForm.value = false;
    newRelationshipDirection.value = RelationshipDirection.OUTGOING;
    newRelationship.value = { type: "", other_id: null };
};

const cancelAddRelationshipForm = resetAddRelationshipForm;

const addRelationship = () => {
    if (!canAddRelationship.value || !entity.value) return;
    formRelationships.value.push({
        type: newRelationship.value.type,
        other_id: newRelationship.value.other_id!,
        direction: newRelationshipDirection.value,
    });
    resetAddRelationshipForm();
};

const removeRelationship = (rel: BusinessEntityRelationship) => {
    formRelationships.value = formRelationships.value.filter(
        (r) => !(r.other_id === rel.other_id && r.direction === rel.direction)
    );
};

// ---------------------------------------------------------------------------
// Dirty tracking — derived, never a latching flag.
// ---------------------------------------------------------------------------

const relKey = (r: BusinessEntityRelationship) =>
    `${r.direction}\0${r.type}\0${r.other_id}`;

// Order-insensitive: the server is free to return relationships in a different
// order than we sent them, and that must not read as a local edit.
const sameRelationships = (
    a: readonly BusinessEntityRelationship[],
    b: readonly BusinessEntityRelationship[]
) => {
    if (a.length !== b.length) return false;
    const left = a.map(relKey).sort();
    const right = b.map(relKey).sort();
    return left.every((key, i) => key === right[i]);
};

const nameError = computed(() =>
    isEditing.value && !formName.value.trim() ? "Name is required." : ""
);

// Populate the quick-edit form from the entity's current (saved) values.
// Deliberately NOT called on save completion: the baselines it diffs against
// (entity.name, entity.relationships, savedDescription) update themselves as
// the server state lands, so there is nothing to re-baseline — and writing
// into the form fields while the user is typing in them is exactly the
// clobber this codebase guards against elsewhere (wouldClobberLocalChanges).
const populateForm = () => {
    const current = entity.value;
    if (!current) return;
    formName.value = current.name;
    formRelationships.value = current.relationships.map((r) => ({ ...r }));
};

interface QuickEditPayload {
    business_entity_id: string;
    name?: string;
    description?: File;
    relationships?: string;
}

// Builds a changed-fields-only payload, matching business-entity-store's
// queueSave(). Returning undefined means "nothing worth persisting" — never
// send a field the user didn't touch, because every field in this payload is
// applied as a full replacement server-side.
const buildPayload = ():
    | {
          body: QuickEditPayload;
          descriptionText?: string;
          // The same relationships `body.relationships` carries as JSON, kept
          // in structured form for the canvas overlay — which needs to reason
          // about individual edges (to mirror them onto the object at the
          // other end), not re-parse a string.
          relationships?: BusinessEntityRelationship[];
      }
    | undefined => {
    const current = entity.value;
    if (!current) return undefined;

    const body: QuickEditPayload = { business_entity_id: current.identity };
    let descriptionText: string | undefined;

    const trimmedName = formName.value.trim();
    if (trimmedName && trimmedName !== current.name) {
        body.name = trimmedName;
    }

    // Only ever send a description we actually loaded. While it's loading or
    // errored, formDescription holds no trustworthy baseline, so sending it
    // could overwrite the real content with an empty or stale value.
    if (
        descriptionState.value === "ready" &&
        formDescription.value !== savedDescription.value
    ) {
        descriptionText = formDescription.value;
        body.description = new File([descriptionText], "description.md", {
            type: "text/markdown",
        });
    }

    let relationships: BusinessEntityRelationship[] | undefined;
    if (!sameRelationships(formRelationships.value, current.relationships)) {
        // Snapshotted, not shared: formRelationships keeps being edited, and
        // the overlay must keep standing behind exactly what was queued.
        relationships = formRelationships.value.map((r) => ({ ...r }));
        body.relationships = JSON.stringify(relationships);
    }

    if (Object.keys(body).length === 1) return undefined;
    return { body, descriptionText, relationships };
};

// Hands the change to the app-wide sync pipeline. Not awaited and not
// error-handled here on purpose: queueSync owns retries, Sentry reporting and
// the failed-sync recovery UI, and the entities list refreshes itself off the
// "synced" broadcast (see business-entities-store), so there is no manual
// refetch — one would tear down and re-centre the canvas on every keystroke.
const performSave = (payload: {
    body: QuickEditPayload;
    descriptionText?: string;
    relationships?: BusinessEntityRelationship[];
}) => {
    // Hands the canvas both halves of the optimistic story, from the moment
    // this save is actually queued (not from the keystroke) — an
    // add-then-remove that nets out to no change before the throttle fires
    // does neither:
    //  - the "Saving..." indicator, for as long as the write is outstanding;
    //  - the new values themselves, which the canvas draws in place of server
    //    truth until the refetch catches up. Without that second half the
    //    object kept its old label for the 5s inactivity debounce plus two
    //    round trips.
    //
    // Name as well as relationships: both are drawn on the canvas and both are
    // in buildDiagramSignature. The description is not on the canvas, so it
    // gets no indicator there — the top bar's autosave status (SyncStatus)
    // covers every field.
    if (
        payload.body.name !== undefined ||
        payload.body.relationships !== undefined
    ) {
        markCanvasSavePending(payload.body.business_entity_id, {
            name: payload.body.name,
            relationships: payload.relationships,
        });
    }
    syncStore.queueSync({
        entityType: EntityType.BUSINESS_ENTITY,
        args: [
            {
                body: payload.body,
                url: "/domain/business_entity_mgt/update_business_entity/start",
            },
        ],
    });
    // Advance the description baseline to what we just queued, mirroring the
    // full page's `description.persist()` after its own queueSync.
    if (payload.descriptionText !== undefined) {
        savedDescription.value = payload.descriptionText;
    }
};

const throttledSave = throttle(performSave, FILE_SYNC_THROTTLE_MS);

// Stable reference — pending-flush stores callbacks in a module-level Set that
// outlives this component, so it must be the same function object to
// unregister, and it must be unregistered (an inline arrow would be a new
// object every mount, leaking one dead closure per mount).
const flushPendingSave = () => throttledSave.flush();
registerFlush(flushPendingSave);

onUnmounted(() => {
    // Flush rather than cancel: an unmount (live-mode toggle, stek switch)
    // must not silently discard a keystroke the user already made.
    throttledSave.flush();
    unregisterFlush(flushPendingSave);
});

// A signature of the whole form, so EVERY change reschedules the save with a
// fresh snapshot. Watching a dirty boolean instead would fire once (it latches
// true and stays there) and a later flush would replay that first stale value.
const formSignature = computed(() =>
    JSON.stringify({
        name: formName.value,
        description: formDescription.value,
        relationships: formRelationships.value.map(relKey).sort(),
    })
);

watch(formSignature, () => {
    if (!isEditing.value) return;
    const payload = buildPayload();
    if (payload) throttledSave(payload);
});

// Populate when a different entity is opened, and fetch its real description.
// immediate:true also covers the panel already being open when the component
// (re)mounts.
watch(
    drawerEntityId,
    (id) => {
        // Must run BEFORE populateForm() below. throttledSave.flush() replays
        // the args captured when it was last scheduled (the OLD entity's id +
        // edited values) — it reads no live refs, so it is safe here even
        // though drawerEntityId.value has already flipped to the NEW id.
        throttledSave.flush();

        // Reset description state so the new entity never shows, or saves, the
        // previous one's text.
        savedDescription.value = "";
        formDescription.value = "";
        descriptionState.value = id ? "loading" : "empty";

        if (!id) return;
        populateForm();
        void loadDescription();
    },
    { immediate: true }
);

// The entity may arrive after the drawer opened (list still loading), or its
// description artefact may appear on a later refetch — fetch once it does.
watch(
    () => entity.value?.identity,
    (identity) => {
        if (!identity) return;
        populateForm();
        if (descriptionState.value === "empty") void loadDescription();
    }
);

// Populate the edit form when the header's Edit/View toggle switches into edit
// mode while the panel is already open on an entity.
watch(isEditing, (editing) => {
    if (editing) populateForm();
});

// Re-baseline relationships when they change on the SERVER while this panel is
// open and the user has no relationship edit of their own pending.
//
// Relationships for the entity this panel is showing can change while it is
// open — from the full entity page in another tab, or via MCP. Without this,
// the new edge would be missing from `formRelationships`, the dirty check would
// then see a difference, and this panel's next autosave would ship its stale
// array and delete that edge: the endpoint replaces the whole list.
//
// "No local edit pending" is decided by comparing against the PREVIOUS server
// value rather than by asking the sync store: if the form still matches what
// the server last said, nothing was changed here, so it is safe to move it on.
// Otherwise the user has unsaved work and theirs must win.
watch(
    () => entity.value?.relationships,
    (serverRelationships, previousServerRelationships) => {
        if (!serverRelationships || !previousServerRelationships) return;
        if (
            sameRelationships(serverRelationships, previousServerRelationships)
        ) {
            return;
        }
        if (
            !sameRelationships(
                formRelationships.value,
                previousServerRelationships
            )
        ) {
            return;
        }
        formRelationships.value = serverRelationships.map((r) => ({ ...r }));
    }
);

// The page this panel was opened on. Two pages open it — the domain page and
// its maximized canvas — but it belongs to whichever one did, not to both: an
// allow-list naming both left a panel opened on the canvas pinned open when
// the user minimized back, on a page they never opened it from.
//
// Seeded from the current route rather than left empty, because the open state
// is app-wide while this component is not: it is only mounted on the pages
// that use it, so it can be mounted with the panel already open.
const openedOnRoute = ref(drawerOpen.value ? String(route.name) : undefined);

// Flush on EVERY close path, not just the close button: the panel is also
// closed by useExclusiveRightDrawers (opening the Threads drawer), which calls
// the store action directly and would otherwise drop a pending edit.
watch(drawerOpen, (open) => {
    if (!open) {
        throttledSave.flush();
        return;
    }
    openedOnRoute.value = String(route.name);
    void nextTick(() => panelRef.value?.focus());
});

// The panel's data is domain-scoped, but its open state is app-wide. Without
// this, navigating to another domain leaves a 420px panel pinned open on an
// entity that is no longer in the list.
watch(
    () => domain.value?.identity,
    (next, previous) => {
        if (previous !== undefined && next !== previous && drawerOpen.value) {
            closeDrawer();
        }
    }
);

// The domain watcher above only catches switching to a DIFFERENT domain — it
// does nothing when navigating to some other page within the SAME domain (e.g.
// one of its Products), so the panel would otherwise stay pinned open on a page
// it doesn't belong on.
watch(
    () => route.name,
    (name) => {
        if (drawerOpen.value && String(name) !== openedOnRoute.value) {
            closeDrawer();
        }
    }
);

const isDeleting = ref(false);

/**
 * A popup anchored to the button on the domain page, a centred modal on the
 * maximized canvas.
 *
 * The canvas page is a single full-screen diagram with no page chrome to fall
 * back on, so a confirmation tucked into the bottom corner of the panel is easy
 * to miss; taking the middle of the screen makes the choice unmissable. The
 * domain page has a table and headings around it, where anchoring to the button
 * keeps the confirmation next to the row it is about.
 */
const confirmGroup = computed(() =>
    route.name === "domainCanvasPage"
        ? CONFIRM_GROUP.dialog
        : CONFIRM_GROUP.popup
);

/**
 * Deleting the object removes its relationships from the objects at the other
 * end too — the backend re-stamps every counterpart, so the canvas comes back
 * without the node OR its arrows. Worth saying plainly, because from here you
 * can only see one end of those edges.
 *
 * `group` is required and is not cosmetic: App.vue mounts one ConfirmPopup
 * (`group="popup"`) and one ConfirmDialog (`group="dialog"`), and PrimeVue
 * matches on strict equality — so the group chooses WHICH of the two appears,
 * and an ungrouped `require` reaches neither, silently.
 */
const confirmDelete = (event: MouseEvent) => {
    const entityId = drawerEntityId.value;
    if (!entityId) return;
    const relationshipCount = entity.value?.relationships?.length ?? 0;

    confirm.require({
        group: confirmGroup.value,
        // Only the popup anchors to this; the dialog uses it to hand focus
        // back to the button afterwards.
        target: event.currentTarget as HTMLElement,
        message: relationshipCount
            ? `Delete '${entity.value?.name}' and its ${relationshipCount} relationship${relationshipCount === 1 ? "" : "s"}? You cannot reference it anymore.`
            : `Delete '${entity.value?.name}'? You cannot reference it anymore.`,
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: { label: "Delete", severity: "danger" },
        accept: async () => {
            isDeleting.value = true;
            try {
                // The store catches its own errors to raise the toast, so it
                // resolves either way — closing on a failed delete would hide
                // a panel whose object is still there, looking like success.
                const deleted = await entitiesStore.deleteEntity(entityId);
                // The panel resolves its id against the domain's entities
                // list, so leaving it open would strand it on the
                // "isn't in this domain any more" state it just caused.
                if (deleted) closeDrawer();
            } finally {
                isDeleting.value = false;
            }
        },
    });
};

const openFullPage = () => {
    if (!drawerEntityId.value) return;
    const businessEntityId = drawerEntityId.value;
    closeDrawer();
    router.push(
        withThreadRevision({
            name: "businessEntityPage",
            params: {
                orgName: route.params.orgName,
                stekName: route.params.stekName,
                businessEntityId,
            },
        })
    );
};
</script>

<style scoped>
/* Same in-layout right-column pattern as ThreadDrawer.vue's .thread-panel.
   The top offset is bound from useRightPanelOffset() rather than hard-coded
   here, so the release banner and thread accent strip are accounted for. */
.entity-panel {
    flex: 0 0 420px;
    width: 420px;
    height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--p-content-background);
    border-left: 1px solid var(--surface-border, #dee2e6);
}

.entity-panel:focus {
    outline: none;
}

.entity-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    min-height: 3rem;
    padding: 0.5rem 0.75rem 0.5rem 1rem;
    border-bottom: 1px solid var(--p-surface-200);
}

.entity-panel-title {
    font-weight: 600;
    font-size: 1.05rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.entity-panel-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    padding: var(--default-gap);
    gap: var(--default-gap);
}

.entity-panel-enter-active,
.entity-panel-leave-active {
    transition:
        opacity 160ms ease,
        transform 160ms ease;
}

.entity-panel-enter-from,
.entity-panel-leave-to {
    opacity: 0;
    transform: translateX(16px);
}

/* A sibling of the scrolling body rather than its last child, so it stays
   pinned to the bottom of the panel however long the object is. `flex: 0 0
   auto` keeps it at its own height while the body takes the rest.
   `space-between` puts the destructive action at the far end, away from the
   one people press often. Saves are automatic, so there is no Save button to
   share this row with. */
.entity-panel-footer {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.5rem var(--default-gap);
    border-top: 1px solid var(--p-surface-200);
    background: var(--p-content-background);
}

.panel-empty {
    display: flex;
    justify-content: center;
    padding: 3rem 0;
}

/* Terminal "this isn't here any more" state — deliberately not a spinner. */
.panel-unavailable {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 2.5rem 1rem;
    text-align: center;
    color: var(--p-text-muted-color);
}

.panel-unavailable i {
    font-size: 1.5rem;
}

.panel-unavailable p {
    margin: 0;
    font-size: 0.875rem;
}

.panel-summary {
    display: flex;
    flex-direction: column;
    gap: var(--default-gap);
}

/* No `white-space: pre-wrap` here, unlike when this showed raw preview text:
   the description is now rendered markdown, so block layout comes from the
   <p>/<ul>/<h*> elements. Preserving the source's newlines on top of that
   would add a stray break after every wrapped line. */
.summary-desc {
    margin: 0;
    font-size: 0.875rem;
    color: var(--p-text-secondary-color);
}

.summary-desc.muted {
    opacity: 0.7;
    font-style: italic;
}

/* Rendered markdown: keep block spacing tight enough for a 420px panel. */
.markdown-body :deep(p),
.markdown-body :deep(ul),
.markdown-body :deep(ol),
.markdown-body :deep(pre) {
    margin: 0 0 0.5rem;
}

.markdown-body :deep(:last-child) {
    margin-bottom: 0;
}

.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3),
.markdown-body :deep(h4) {
    margin: 0.75rem 0 0.35rem;
    font-size: 0.9rem;
}

.markdown-body :deep(pre) {
    overflow-x: auto;
    padding: 0.5rem;
    background: var(--p-surface-100);
    border-radius: 4px;
}

.markdown-body :deep(ul),
.markdown-body :deep(ol) {
    padding-left: 1.25rem;
}

/* Section headings (Description/Relationships) use the app's standard
   heading color/weight so they read consistently with the rest of the app
   (and adapt automatically between light/dark themes). */
.section-title {
    margin: 0;
    font-size: 1rem;
}

/* Smaller labels for a single field within a section (Outgoing/Incoming,
   New relationship) — deliberately a step down from .section-title. */
.field-label {
    margin: 0;
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--p-text-muted-color);
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.summary-value.muted {
    font-size: 0.875rem;
    font-style: italic;
    opacity: 0.7;
}

.summary-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.relationship-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 0;
    padding: 0;
    list-style: none;
}

.relationship-list li {
    font-size: 0.875rem;
}

/* Same colors as BusinessEntityPage.vue's relationship sentences: the
   related entity's name in the app's primary color, the relationship
   type in italics. */
.entity-reference {
    color: var(--stekz-primary-color);
    font-weight: bold;
}

.relationship-type {
    padding: 0 0.4rem;
    font-style: italic;
}

/* A bordered card, distinct from the plain Name/Description fields above —
   Relationships is a multi-part section (list + add form), not a single
   input, so it gets its own visual boundary rather than blending into the
   rest of the form. */
.relationships-section {
    display: flex;
    flex-direction: column;
    gap: var(--default-gap);
    padding: 0.75rem;
    border: 1px solid var(--p-surface-200);
    border-radius: 8px;
}

/* Separated from the existing-relationships list above by its own divider,
   so "add a new one" reads as a distinct action, not another list row. */
.rel-add-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--p-surface-200);
}

/* Add-relationship controls stack full-width — this 420px panel has no room
   for the full page's side-by-side .form-row layout. */
.rel-add-form {
    display: flex;
    flex-direction: column;
    gap: var(--default-gap);
}

/* PrimeVue's SelectButton doesn't forward a plain "w-full" class to its
   rendered root the way Button/Select do, so it's targeted through a
   stable DOM ancestor (.rel-add-form) instead: stretch the group to the
   same width as the other controls, and split that width evenly between
   the two direction buttons rather than leaving them at their own (much
   narrower) content width. */
.rel-add-form :deep(.p-selectbutton) {
    width: 100%;
}

.rel-add-form :deep(.p-togglebutton) {
    flex: 1;
}

/* The relationship-type Select's "editable" mode renders its label as a
   plain <input>, which (unlike PrimeVue's other form-field elements) isn't
   tagged with the class the app's font-family rule targets, so it falls
   back to the browser's default UI font instead of the site's typeface —
   visually mismatched against every other field in this panel. */
.rel-add-form :deep(.p-select-label) {
    font-family: inherit;
}

.rel-add-form-actions {
    display: flex;
    gap: 0.5rem;
}

.rel-add-form-actions > * {
    flex: 1;
}

/* View mode's Outgoing/Incoming groups — kept separate per direction (unlike
   edit mode's single merged list), but sharing the same standard spacing
   scale (0.5rem) as every other grouping in this panel. margin-top applies
   to every group, including the first one right under the "Relationships"
   title: .summary-section's own 0.5rem gap already separates the title from
   the first group and each group's own heading from its rows, so without
   this extra margin, "Relationships" -> "Outgoing" and "Outgoing"'s rows ->
   "Incoming" would read at the exact same tight rhythm as a label-to-its-
   own-content gap — collapsing the title and both directions into what
   looks like one continuous list instead of distinct sections. */
.relationship-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.relationship-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.relationship-direction-icon {
    flex-shrink: 0;
    font-size: 0.75rem;
    color: var(--p-text-muted-color);
}

.relationship-row-sentence {
    flex: 1;
}

.panel-form {
    display: flex;
    flex-direction: column;
    gap: var(--default-gap);
}

.field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.field label {
    font-weight: 500;
    font-size: 0.9rem;
}

.field-error {
    color: var(--p-red-500, #ef4444);
    font-size: 0.8rem;
}

.field-loading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
    color: var(--p-text-muted-color);
}

.field-error-block {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    font-size: 0.8rem;
    color: var(--p-text-muted-color);
}

.field-error-block p {
    margin: 0;
}

/* "w-full" isn't a global utility in this app — every component that uses it
   defines its own scoped rule (see e.g. UserManagement.vue, OAuthAuthorize.vue).
   Without this, PrimeVue's InputText/Select already default to full width,
   but Textarea falls back to its native intrinsic (cols-based) width. */
.w-full {
    width: 100%;
}

.panel-form-hint {
    margin: 0;
    font-size: 0.8rem;
    font-style: italic;
    text-align: left;
    color: var(--p-text-muted-color);
}
</style>

<style>
/* Unscoped on purpose: PrimeVue teleports a Select's option-list overlay to
   <body>, outside this component's DOM subtree, so scoped styles (and
   :deep()) can never reach it — overlayClass is the one hook PrimeVue gives
   for styling it. size="small" on the two relationship Selects already
   shrinks their closed field to match the rest of this panel, but PrimeVue's
   own "small" sizing doesn't extend to the option rows in the opened list,
   which stay at the default 1rem — this brings them down to match. Scoped to
   .rel-add-dropdown-overlay (not a bare .p-select-option rule) so it can't
   affect any other Select elsewhere in the app. */
.rel-add-dropdown-overlay .p-select-option {
    font-size: var(--p-form-field-sm-font-size);
}
</style>
